import React, { useState, useEffect } from "react";
import {
  Phone,
  Mail,
  Users,
  CheckCircle,
  AlertCircle,
  Bell,
  UserPlus,
} from "lucide-react";
import { useHistory } from "react-router-dom";
import { useAppContext } from "../../AppContext";
import { MyDomain } from "../../App";
import axios from "axios";
import { useTranslation } from "react-i18next";
import AccountantInviteModal from "./AccountantContractorComps/AccountantInviteModal";
import "./AccountantHome.css";

export default function AccountantHome() {
  const { t } = useTranslation();
  const history = useHistory();
  const { User, Lang } = useAppContext();
  const isRTL = Lang === "ar" || Lang === "he";

  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteModalVisible, setInviteModalVisible] = useState(false);

  useEffect(() => {
    fetchContractors();
  }, []);

  const fetchContractors = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${MyDomain}Accountant/AccountantGetAllContractors`,
        {
          Id: User.id,
        }
      );

      if (response.data && response.data.myContractors) {
        setContractors(response.data.myContractors);
        console.log("✅ Contractors loaded:", response.data.myContractors);
      }
    } catch (error) {
      console.error("❌ Error fetching contractors:", error);
      alert(t("failedToLoadContractors"));
    } finally {
      setLoading(false);
    }
  };

  const handleContractorPress = (contractor) => {
    // Just navigate - AccountantHomeContractorMainPage will fetch the data
    history.push({
      pathname: "/accountant/contractor-details",
      state: {
        contractorId: contractor.id,
        contractorName: contractor.name,
        contractorData: contractor,
      },
    });
  };

  const handleCall = (phoneNumber, event) => {
    if (event) {
      event.stopPropagation();
    }
    if (phoneNumber) {
      window.location.href = `tel:${phoneNumber}`;
    } else {
      alert(t("phoneNumberNotAvailable"));
    }
  };

  const handleEmail = (email, event) => {
    if (event) {
      event.stopPropagation();
    }
    if (email) {
      window.location.href = `mailto:${email}`;
    } else {
      alert(t("emailNotAvailable"));
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";
    const words = name.trim().split(" ");
    if (words.length === 1) return name.substring(0, 2).toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  };

  const getStatusColor = () => {
    return "#34C759";
  };

  const getStatusText = () => {
    return t("active");
  };

  const renderContractorCard = (item, index) => (
    <div
      key={item.id}
      className={`contractor-card ${isRTL ? "rtl" : ""}`}
      onClick={() => handleContractorPress(item)}
      style={{ animationDelay: `${index * 100}ms` }}
    >
      {/* Header with Avatar and Info */}
      <div className={`card-header ${isRTL ? "rtl" : ""}`}>
        <div className="avatar-container">
          <div
            className="avatar"
            style={{ backgroundColor: getStatusColor() + "20" }}
          >
            <span className="avatar-text" style={{ color: getStatusColor() }}>
              {getInitials(item.name)}
            </span>
          </div>
          <div
            className="status-badge"
            style={{ backgroundColor: getStatusColor() }}
          />
        </div>

        <div className={`contractor-info ${isRTL ? "rtl" : ""}`}>
          <h3 className={`contractor-name ${isRTL ? "rtl" : ""}`}>
            {item.name}
          </h3>
          <div className={`status-container ${isRTL ? "rtl" : ""}`}>
            <div
              className="status-pill"
              style={{ backgroundColor: getStatusColor() + "20" }}
            >
              <span className="status-text" style={{ color: getStatusColor() }}>
                {getStatusText()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Info */}
      <div className="contact-section">
        {item.phoneNumber && (
          <div
            className={`contact-item ${isRTL ? "rtl" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              handleCall(item.phoneNumber);
            }}
          >
            <div className="icon-wrapper">
              <Phone size={18} color="#007AFF" />
            </div>
            <span className={`contact-text ${isRTL ? "rtl" : ""}`}>
              {item.phoneNumber}
            </span>
          </div>
        )}

        {item.email && (
          <div
            className={`contact-item ${isRTL ? "rtl" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              handleEmail(item.email);
            }}
          >
            <div className="icon-wrapper">
              <Mail size={18} color="#007AFF" />
            </div>
            <span className={`contact-text ${isRTL ? "rtl" : ""}`}>
              {item.email}
            </span>
          </div>
        )}

        {!item.phoneNumber && !item.email && (
          <div className="no-contact-container">
            <AlertCircle size={16} color="#999" />
            <span className={`no-contact-text ${isRTL ? "rtl" : ""}`}>
              {t("noContactInfo")}
            </span>
          </div>
        )}
      </div>

      {/* Footer with ID */}
      <div className={`card-footer ${isRTL ? "rtl" : ""}`}>
        <span className={`id-label ${isRTL ? "rtl" : ""}`}>
          {t("id")}: {item.id}
        </span>
        {item.expoToken && (
          <div className="notification-badge">
            <Bell size={14} color="#007AFF" />
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p className={`loading-text ${isRTL ? "rtl" : ""}`}>
          {t("loadingContractors")}
        </p>
      </div>
    );
  }

  return (
    <div className={`accountant-home-container ${isRTL ? "rtl" : ""}`}>
      {/* Header */}
      <div className="header">
        <div className={`header-content ${isRTL ? "rtl" : ""}`}>
          <div>
            <h1 className={`header-title ${isRTL ? "rtl" : ""}`}>
              {t("myContractors")}
            </h1>
            <p className={`header-subtitle ${isRTL ? "rtl" : ""}`}>
              {contractors.length} {t("contractors")}
            </p>
          </div>

          {/* Header Actions with Invite Button */}
          <div className="header-actions">
            <button
              className="invite-button"
              onClick={() => setInviteModalVisible(true)}
              title={t("inviteContractor")}
            >
              <UserPlus size={24} color="#fff" />
            </button>
            <div className="header-icon-container">
              <Users size={32} color="#007AFF" />
            </div>
          </div>
        </div>
      </div>

      {/* Stats Card */}
      <div className="single-stat-container">
        <div className="single-stat-card">
          <div
            className="stat-icon-container"
            style={{ backgroundColor: "#34C75920" }}
          >
            <CheckCircle size={28} color="#34C759" />
          </div>
          <div className="stat-content">
            <h2 className={`stat-number ${isRTL ? "rtl" : ""}`}>
              {contractors.length}
            </h2>
            <p className={`stat-label ${isRTL ? "rtl" : ""}`}>
              {t("activeContractors")}
            </p>
          </div>
        </div>
      </div>

      {/* Contractors List */}
      {contractors.length === 0 ? (
        <div className="empty-container">
          <Users size={80} color="#CCC" />
          <h2 className={`empty-text ${isRTL ? "rtl" : ""}`}>
            {t("noContractorsFound")}
          </h2>
          <p className={`empty-subtext ${isRTL ? "rtl" : ""}`}>
            {t("contractorsWillAppearHere")}
          </p>
        </div>
      ) : (
        <div className="contractors-grid">
          {contractors.map((item, index) => renderContractorCard(item, index))}
        </div>
      )}

      {/* Invite Modal */}
      {inviteModalVisible && (
        <AccountantInviteModal
          visible={inviteModalVisible}
          onClose={() => setInviteModalVisible(false)}
          accountantId={User.id}
          onInviteSent={() => {
            fetchContractors();
          }}
        />
      )}
    </div>
  );
}
